/*
 * ApplicationReviewModal — "Call, Discuss, Decide" unified review screen.
 *
 * Opens on top of ApplicationsPanel (Admin) or ApprovalsLane (Floor) for a
 * single pending TP or driver application. Puts the application details, a
 * one-tap click-to-call, a permanent notes thread (with optional department
 * tagging), and the existing Approve/Reject actions on the same screen —
 * no tab-switching, no separate call log to check.
 */
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { toast } from "sonner";
import { Phone, X, Send, Loader2 } from "lucide-react";
import ApplicationFields from "./ApplicationFields";
import RequestDocsButton from "./RequestDocsButton";

const C = { forest: "#2B4C3B", ink: "#0F172A", dim: "#475569", line: "#E2E0D8", amber: "#B45309", red: "#B91C1C" };

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const OUTCOME_STYLE = {
  answered: { bg: "#ECFDF5", color: "#047857", label: "Answered" },
  no_answer: { bg: "#FFFBEB", color: C.amber, label: "No answer" },
  busy: { bg: "#FFFBEB", color: C.amber, label: "Busy" },
  failed: { bg: "#FEF2F2", color: C.red, label: "Failed" },
  canceled: { bg: "#FEF2F2", color: C.red, label: "Canceled" },
  unknown: { bg: "#F1F5F9", color: C.dim, label: "Unknown" },
};

export default function ApplicationReviewModal({ appType, appId, onClose, onApprove, onReject }) {
  const [app, setApp] = useState(null);
  const [calls, setCalls] = useState([]);
  const [notes, setNotes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  const [posting, setPosting] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteDept, setNoteDept] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [appRes, callsRes, notesRes, deptRes] = await Promise.all([
        apiClient.get(`/applications/${appType}/${appId}`),
        apiClient.get(`/applications/${appType}/${appId}/calls`),
        apiClient.get(`/applications/${appType}/${appId}/notes`),
        apiClient.get(`/applications/departments`),
      ]);
      setApp(appRes.data);
      setCalls(callsRes.data || []);
      setNotes(notesRes.data || []);
      setDepartments(deptRes.data?.departments || []);
    } catch (e) {
      toast.error("Could not load this application");
    } finally {
      setLoading(false);
    }
  }, [appType, appId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const placeCall = async () => {
    if (!app?.phone) return;
    if (!window.confirm(`Call ${app.display_name || "this applicant"} at ${app.phone} now?`)) return;
    setCalling(true);
    try {
      await apiClient.post(`/applications/${appType}/${appId}/call`);
      toast.success("Calling now — connecting you in");
      setTimeout(() => {
        apiClient.get(`/applications/${appType}/${appId}/calls`).then((r) => setCalls(r.data || []));
      }, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place the call");
    } finally {
      setCalling(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      const { data } = await apiClient.post(`/applications/${appType}/${appId}/notes`, {
        text: noteText.trim(), department: noteDept || null,
      });
      setNotes((prev) => [...prev, data]);
      setNoteText("");
      setNoteDept("");
      toast.success(noteDept ? `Note added — ${noteDept} alerted` : "Note added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add note");
    } finally {
      setPosting(false);
    }
  };

  const runDecision = async (fn) => {
    setDecisionBusy(true);
    try {
      const result = await fn();
      if (result !== false) onClose();
    } finally {
      setDecisionBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)" }}
      data-testid="application-review-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        data-testid="application-review-modal"
      >
        <div className="sticky top-0 flex items-center justify-between p-4 border-b bg-white rounded-t-2xl" style={{ borderColor: C.line }}>
          <div>
            <div className="text-lg font-bold" style={{ color: C.ink }}>
              {app?.display_name || "Application"}
            </div>
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: C.dim }}>
              {appType === "tp" ? "Transportation Partner" : "Independent Driver"} · {app?.status || "—"}
            </div>
          </div>
          <button onClick={onClose} data-testid="application-review-close-btn" className="p-2 rounded-full hover:bg-gray-100">
            <X size={20} color={C.dim} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: C.dim }}>Loading…</div>
        ) : (
          <div className="p-4 space-y-5">
            {/* Click-to-call pill */}
            <div className="flex items-center gap-3">
              <button
                onClick={placeCall}
                disabled={calling || !app?.phone}
                data-testid="application-review-call-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm text-white disabled:opacity-50"
                style={{ background: C.forest }}
              >
                {calling ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
                {app?.phone || "No phone on file"}
              </button>
              <span className="text-xs" style={{ color: C.dim }}>{app?.email}</span>
            </div>

            <ApplicationFields appType={appType} fields={app?.fields} />

            {/* Call history */}
            <div>
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: C.dim }}>Call history</div>
              {calls.length === 0 ? (
                <div className="text-sm" style={{ color: C.dim }}>No calls placed yet.</div>
              ) : (
                <div className="space-y-1.5" data-testid="application-review-call-history">
                  {calls.map((c) => {
                    const style = c.outcome ? (OUTCOME_STYLE[c.outcome] || OUTCOME_STYLE.unknown) : null;
                    return (
                      <div key={c.call_id} className="flex items-center justify-between text-sm rounded-lg px-3 py-2" style={{ background: "#F8F7F2" }}>
                        <div>
                          <span style={{ color: C.ink }}>{(c.started_at || "").replace("T", " ").slice(0, 16)}</span>
                          <span className="ml-2 text-xs" style={{ color: C.dim }}>by {c.placed_by}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: C.dim }}>{formatDuration(c.duration_seconds)}</span>
                          {style ? (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: style.bg, color: style.color }}>
                              {style.label}
                            </span>
                          ) : (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#EFF6FF", color: "#1D4ED8" }}>
                              In progress…
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Notes thread */}
            <div>
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: C.dim }}>Notes</div>
              <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="application-review-notes-thread">
                {notes.length === 0 ? (
                  <div className="text-sm" style={{ color: C.dim }}>No notes yet — add one after your call.</div>
                ) : notes.map((n) => (
                  <div key={n.note_id} className="rounded-lg px-3 py-2 text-sm" style={{ background: "#F8F7F2" }} data-testid={`application-note-${n.note_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold" style={{ color: C.ink }}>{n.author_name}</span>
                      <span className="text-xs" style={{ color: C.dim }}>{(n.created_at || "").replace("T", " ").slice(0, 16)}</span>
                    </div>
                    <div style={{ color: C.ink }}>{n.text}</div>
                    {n.department && (
                      <span className="inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: C.amber }}>
                        → {n.department}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2 items-start">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="e.g. Agreed $2.10/mile, covers downtown + north side"
                  rows={2}
                  data-testid="application-note-input"
                  className="flex-1 rounded-lg border p-2 text-sm resize-none"
                  style={{ borderColor: C.line }}
                />
                <div className="flex flex-col gap-1.5">
                  <select
                    value={noteDept}
                    onChange={(e) => setNoteDept(e.target.value)}
                    data-testid="application-note-department-select"
                    className="rounded-lg border p-1.5 text-xs"
                    style={{ borderColor: C.line, color: C.ink }}
                  >
                    <option value="">No department</option>
                    {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <button
                    onClick={addNote}
                    disabled={posting || !noteText.trim()}
                    data-testid="application-note-submit-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-50"
                    style={{ background: C.forest }}
                  >
                    <Send size={13} />
                    {noteDept ? `Alert ${noteDept}` : "Add note"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approve / Reject — unchanged, now with full context above them */}
        <div className="sticky bottom-0 flex justify-between gap-2 p-4 border-t bg-white rounded-b-2xl" style={{ borderColor: C.line }}>
          <RequestDocsButton kind={appType === "tp" ? "tp_app" : "driver"} recordId={appId} />
          <div className="flex gap-2">
          <button
            onClick={() => runDecision(onReject)}
            disabled={decisionBusy}
            data-testid="application-review-reject-btn"
            className="px-4 py-2 rounded-md text-sm font-semibold border disabled:opacity-60"
            style={{ borderColor: C.line, color: C.red }}
          >
            Reject
          </button>
          <button
            onClick={() => runDecision(onApprove)}
            disabled={decisionBusy}
            data-testid="application-review-approve-btn"
            className="px-4 py-2 rounded-md text-sm font-bold text-white disabled:opacity-60"
            style={{ background: C.forest }}
          >
            Approve
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}
